import CloseIcon from "@mui/icons-material/Close"; // For a close button
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slider,
} from "@mui/material";
import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";

const FixedSizeImageCropper = ({
  open,
  image,
  aspectRatio, // Aspect ratio for the crop box (e.g., 16/9, 4/3)
  maxSize, // Maximum size of the cropped image (e.g., { width: 1280, height: 720 })
  handleCropComplete, // Callback function to handle the cropped image
  onClose,
  roundCropAreaPixels = false,
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Handle crop completion
  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  // Save the cropped image
  const handleSaveCroppedImage = async () => {
    try {
      if (!croppedAreaPixels) return;

      let fileType = "image/jpeg"; // default
      let fileExt = "jpg"; // default

      if (image instanceof File) {
        // Case 1: Direct File
        fileType = image.type || fileType;
        fileExt = image.name.split(".").pop().toLowerCase();
      } else if (typeof image === "string" && image.startsWith("data:")) {
        // Case 2: Base64 Data URL from FileReader
        const match = image.match(/^data:(image\/[a-zA-Z0-9+]+);base64,/);
        if (match) {
          fileType = match[1];
          fileExt = fileType.split("/")[1];
        }
      } else if (typeof image === "string") {
        // Case 3: Image URL
        const match = image.match(/\.(\w+)(\?.*)?$/);
        if (match) {
          fileExt = match[1].toLowerCase();
          if (fileExt === "jpg") fileExt = "jpeg"; // normalize
          fileType = `image/${fileExt}`;
        }
      }

      // Get cropped blob in the original format
      const croppedBlob = await getCroppedImg(
        image,
        croppedAreaPixels,
        maxSize,
        fileType // Ensure we pass correct type
      );

      // Create file with the correct type/extension
      const fileName = `cropped-${Date.now()}.${fileExt}`;
      const croppedFile = new File([croppedBlob], fileName, { type: fileType });

      // Pass file to parent
      handleCropComplete(croppedFile);

      // Close modal
      onClose();
    } catch (error) {
      console.error("Error cropping the image:", error);
    }
  };

  const getCroppedImg = (imageSrc, pixelCrop, maxSize, fileType) => {
    const image = new Image();
    image.src = imageSrc;

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    // Calculate the final dimensions based on maxSize and aspect ratio
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const cropWidth = pixelCrop.width * scaleX;
    const cropHeight = pixelCrop.height * scaleY;

    // Set canvas dimensions to the cropped area
    canvas.width = Math.min(cropWidth, maxSize.width);
    canvas.height = Math.min(cropHeight, maxSize.height);

    // Draw the cropped image onto the canvas
    ctx.drawImage(
      image,
      pixelCrop.x * scaleX,
      pixelCrop.y * scaleY,
      cropWidth,
      cropHeight,
      0,
      0,
      canvas.width,
      canvas.height
    );

    // Return the cropped image as a blob
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, fileType);
    });
  };

  return (
    <Dialog
      open={open} // Open the modal if an image is provided
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="crop-dialog-title"
      PaperProps={{
        sx: {
          overflow: "hidden", // Ensure content doesn't overflow
        },
      }}
    >
      {/* Dialog Title with Close Button */}
      <DialogTitle
        id="crop-dialog-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: "1px solid #e0e0e0", // Subtle border
        }}
        className="py-2"
      >
        Crop Image
        <IconButton color="inherit" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: "24px !important",
          paddingTop: "24px !important",
          position: "relative",
        }}
      >
        {/* Cropper Container */}
        <Box
          sx={{
            width: "calc(100% - 48px)", // Adjust width to accommodate the zoom slider
            height: "400px",
            position: "relative",
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: "transparent", // Remove black background
          }}
        >
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            restrictPosition
            minZoom={1}
            maxZoom={3}
            cropShape={roundCropAreaPixels ? "round" : "rect"} // Ensure rectangular crop
            showGrid={true} // Hide the grid
            style={{
              containerStyle: { backgroundColor: "transparent" }, // Remove black background
              mediaStyle: { objectFit: "cover" }, // Ensure the image fully covers the container
            }}
          />
        </Box>

        {/* Vertical Zoom Slider */}
        <Box
          sx={{
            position: "absolute",
            right: "16px",
            top: "50%",
            transform: "translateY(-50%)",
            height: "300px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Slider
            value={zoom}
            min={1}
            max={3}
            step={0.1}
            onChange={(e, newValue) => setZoom(newValue)}
            orientation="vertical" // Make the slider vertical
            sx={{
              color: "#1976d2", // Primary color for the slider
              height: "100%",
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions className="border-top px-3 py-2">
        <Button variant="outlined" onClick={onClose} size="small">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveCroppedImage}
          size="small"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FixedSizeImageCropper;
